import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import PolicyDocuments from '../components/PolicyDocuments';
import './Pages.css';

const EditPolicy = ({ policyId, setActiveMenu }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [products, setProducts] = useState([]);
  const [insuredItems, setInsuredItems] = useState([]);
  const [selectedInsuredItem, setSelectedInsuredItem] = useState(null);
  const [vehicleValue, setVehicleValue] = useState('');
  const [capacity, setCapacity] = useState('');
  const [quoteBreakdown, setQuoteBreakdown] = useState(null);
  
  const [clientData, setClientData] = useState({
    FirstName: '',
    LastName: '',
    Surname: '',
    Email: '',
    Phone: '',
    KraPin: '',
    IDNumber: ''
  });
  
  const [vehicleData, setVehicleData] = useState({
    Registration: '',
    Make: '',
    Model: '',
    Chassis: '',
    Engine: '',
    YOM: '',
    CC: '',
    Color: '',
    CommencementDate: '',
    ExpiryDate: ''
  });

  useEffect(() => {
    fetchPolicy();
    fetchProducts();
  }, [policyId]);

  const fetchPolicy = async () => {
    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/policies/${policyId}`);
      const data = await response.json();

      if (data.success) {
        const policyData = data.data;
        setPolicy(policyData);
        
        // Set client data
        setClientData({
          FirstName: policyData.FirstName || '',
          LastName: policyData.LastName || '',
          Surname: policyData.Surname || '',
          Email: policyData.CustomerEmail || '',
          Phone: policyData.Phone || '',
          KraPin: policyData.KraPin || '',
          IDNumber: policyData.IDNumber || ''
        });

        // Set vehicle data
        setVehicleData({
          Registration: policyData.Registration || '',
          Make: policyData.Make || '',
          Model: policyData.Model || '',
          Chassis: policyData.Chassis || '',
          Engine: policyData.Engine || '',
          YOM: policyData.YOM || '',
          CC: policyData.CC || '',
          Color: policyData.Color || '',
          CommencementDate: policyData.CommencementDate || '',
          ExpiryDate: policyData.ExpiryDate || ''
        });

        setVehicleValue(policyData.SumInsured || '');
        setCapacity(policyData.Capacity || '');

        // Set quote breakdown
        const basicPremium = policyData.GrossPremium - (policyData.GrossPremium * 0.0025) - (policyData.GrossPremium * 0.002);
        setQuoteBreakdown({
          basicPremium: basicPremium,
          phcf: policyData.GrossPremium * 0.0025,
          trainingLevy: policyData.GrossPremium * 0.002,
          stampDuty: 0,
          grossPremium: policyData.GrossPremium
        });

        // Fetch insured items for the product
        if (policyData.Product) {
          fetchInsuredItems(policyData.Product);
        }
      }
    } catch (err) {
      console.error('Error fetching policy:', err);
      alert('Failed to load policy details');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/products');
      const data = await response.json();
      if (data.success) {
        setProducts(data.products.filter(p => p.Status === 1));
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchInsuredItems = async (productId) => {
    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/products/${productId}`);
      const data = await response.json();
      if (data.success) {
        setInsuredItems(data.insuredItems);
        // Find and set the current insured item
        const currentItem = data.insuredItems.find(item => item.InsuredItemName === policy?.CoverType);
        if (currentItem) {
          setSelectedInsuredItem(currentItem);
        }
      }
    } catch (err) {
      console.error('Error fetching insured items:', err);
    }
  };

  const calculateQuote = async () => {
    if (!selectedInsuredItem) {
      alert('Please select cover type');
      return;
    }

    if (selectedInsuredItem.CalculationType === 1 && !vehicleValue) {
      alert('Vehicle value is required for this cover type');
      return;
    }

    if (selectedInsuredItem.CalculationType === 3 && !capacity) {
      alert('Seating capacity is required for this cover type');
      return;
    }

    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/calculate-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          insuredItemId: selectedInsuredItem.Id,
          vehicleValue: parseFloat(vehicleValue) || 0,
          capacity: capacity ? parseInt(capacity) : null
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setQuoteBreakdown(data.breakdown);
      } else {
        alert(data.message || 'Failed to calculate quote');
      }
    } catch (err) {
      console.error('Error calculating quote:', err);
      alert('Failed to calculate quote');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount || 0);
  };

  const handleClientDataChange = (e) => {
    setClientData({
      ...clientData,
      [e.target.name]: e.target.value
    });
  };

  const handleVehicleDataChange = (e) => {
    const { name, value } = e.target;
    setVehicleData({
      ...vehicleData,
      [name]: value
    });

    if (name === 'CommencementDate' && value) {
      const commencementDate = new Date(value);
      const expiryDate = new Date(commencementDate);
      expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      expiryDate.setDate(expiryDate.getDate() - 1);
      
      setVehicleData(prev => ({
        ...prev,
        ExpiryDate: expiryDate.toISOString().split('T')[0]
      }));
    }
  };

  const handleUpdatePolicy = async () => {
    if (!clientData.Email || !clientData.FirstName || !clientData.LastName) {
      alert('Please fill in all required client fields');
      return;
    }

    if (!vehicleData.Registration || !vehicleData.Make || !vehicleData.Model || 
        !vehicleData.CommencementDate || !vehicleData.ExpiryDate) {
      alert('Please fill in all required vehicle fields');
      return;
    }

    if (!quoteBreakdown) {
      alert('Please recalculate quote before updating');
      return;
    }

    setSubmitting(true);

    try {
      const userEmail = localStorage.getItem('userEmail');
      
      const response = await fetch('https://main.victriedemos.com:3017/api/update-policy', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policyId: policyId,
          client: clientData,
          vehicle: {
            ...vehicleData,
            SumInsured: parseFloat(vehicleValue) || 0,
            Capacity: capacity ? parseInt(capacity) : null
          },
          policy: {
            Product: policy.Product,
            CoverType: selectedInsuredItem?.InsuredItemName || policy.CoverType,
            GrossPremium: quoteBreakdown.grossPremium,
            Commission: quoteBreakdown.grossPremium * 0.10,
            CommencementDate: vehicleData.CommencementDate,
            ExpiryDate: vehicleData.ExpiryDate,
            UpdatedBy: userEmail
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Policy updated successfully!');
        setActiveMenu('new-policies');
      } else {
        alert('❌ Failed to update policy: ' + data.message);
      }
    } catch (err) {
      console.error('Error updating policy:', err);
      alert('❌ An error occurred while updating policy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All changes will be lost.')) {
      setActiveMenu('new-policies');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading policy details...</p>
        </div>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="page-container">
        <div className="error-banner">
          Policy not found
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>✏️ Edit Policy</h1>
          <p className="page-subtitle">Update policy details - {policy.PolicyNumber}</p>
        </div>
        <button className="back-to-policies-btn" onClick={handleCancel}>
          ← Cancel
        </button>
      </div>

      <div className="form-container-narrow">
        <div className="step-content">
          <h2>💰 Premium Calculation</h2>
          <p>Policy id: {policy.Id}</p>
          <div className="form-grid">
            <div className="form-group-icon">
              <label>📦 Product</label>
              <input
                type="text"
                value={products.find(p => p.Id === policy.Product)?.ProductName || 'N/A'}
                className="form-input-narrow"
                readOnly
                disabled
              />
            </div>

            <div className="form-group-icon">
              <label>🛡️ Cover Type *</label>
              <select 
                value={selectedInsuredItem?.Id || ''} 
                onChange={(e) => {
                  const item = insuredItems.find(i => i.Id === parseInt(e.target.value));
                  setSelectedInsuredItem(item);
                  setQuoteBreakdown(null);
                }}
                className="form-input-narrow"
              >
                <option value="">-- Select Cover Type --</option>
                {insuredItems.map(item => (
                  <option key={item.Id} value={item.Id}>
                    {item.InsuredItemName} - {item.CalculationType === 1 ? `${item.Rate}%` : 
                     item.CalculationType === 2 ? formatCurrency(item.Rate) : 'Rate Table'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group-icon">
              <label>💵 Vehicle Value (KES) {selectedInsuredItem?.CalculationType === 1 ? '*' : ''}</label>
              <input
                type="number"
                value={vehicleValue}
                onChange={(e) => {
                  setVehicleValue(e.target.value);
                  setQuoteBreakdown(null);
                }}
                className="form-input-narrow"
                placeholder="Enter vehicle value"
                disabled={selectedInsuredItem?.CalculationType === 2}
              />
            </div>

            <div className="form-group-icon">
              <label>💺 Seating Capacity {selectedInsuredItem?.CalculationType === 3 ? '*' : ''}</label>
              <input
                type="number"
                value={capacity}
                onChange={(e) => {
                  setCapacity(e.target.value);
                  setQuoteBreakdown(null);
                }}
                className="form-input-narrow"
                placeholder="Enter seating capacity"
              />
            </div>
          </div>

          <div className="form-actions-center">
            <button 
              type="button" 
              className="primary-btn-narrow"
              onClick={calculateQuote}
              disabled={!selectedInsuredItem}
            >
              🧮 Recalculate Premium
            </button>
          </div>

          {quoteBreakdown && (
            <div className="quote-breakdown">
              <h3>Premium Breakdown</h3>
              <div className="breakdown-table">
                <div className="breakdown-row">
                  <span>Basic Premium:</span>
                  <strong>{formatCurrency(quoteBreakdown.basicPremium)}</strong>
                </div>
                <div className="breakdown-row">
                  <span>PHCF (0.25%):</span>
                  <span>{formatCurrency(quoteBreakdown.phcf)}</span>
                </div>
                <div className="breakdown-row">
                  <span>Training Levy (0.2%):</span>
                  <span>{formatCurrency(quoteBreakdown.trainingLevy)}</span>
                </div>
                <div className="breakdown-row">
                  <span>Stamp Duty:</span>
                  <span>{formatCurrency(quoteBreakdown.stampDuty)}</span>
                </div>
                <div className="breakdown-row total">
                  <span><strong>Gross Premium:</strong></span>
                  <strong className="gross-premium">{formatCurrency(quoteBreakdown.grossPremium)}</strong>
                </div>
              </div>
            </div>
          )}

          <h2>👤 Client Information</h2>
          
          <div className="form-grid">
            <div className="form-group-icon">
              <label>👤 First Name *</label>
              <input
                type="text"
                name="FirstName"
                value={clientData.FirstName}
                onChange={handleClientDataChange}
                className="form-input-narrow"
              />
            </div>

            <div className="form-group-icon">
              <label>👤 Last Name *</label>
              <input
                type="text"
                name="LastName"
                value={clientData.LastName}
                onChange={handleClientDataChange}
                className="form-input-narrow"
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group-icon">
              <label>👥 Surname</label>
              <input
                type="text"
                name="Surname"
                value={clientData.Surname}
                onChange={handleClientDataChange}
                className="form-input-narrow"
              />
            </div>

            <div className="form-group-icon">
              <label>📧 Email *</label>
              <input
                type="email"
                name="Email"
                value={clientData.Email}
                onChange={handleClientDataChange}
                className="form-input-narrow"
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group-icon">
              <label>📱 Phone *</label>
              <input
                type="tel"
                name="Phone"
                value={clientData.Phone}
                onChange={handleClientDataChange}
                className="form-input-narrow"
              />
            </div>

            <div className="form-group-icon">
              <label>🆔 ID Number *</label>
              <input
                type="text"
                name="IDNumber"
                value={clientData.IDNumber}
                onChange={handleClientDataChange}
                className="form-input-narrow"
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group-icon">
              <label>🏦 KRA PIN</label>
              <input
                type="text"
                name="KraPin"
                value={clientData.KraPin}
                onChange={handleClientDataChange}
                className="form-input-narrow"
              />
            </div>
          </div>

          <h2>🚗 Vehicle Details</h2>
          
          <div className="form-grid">
            <div className="form-group-icon">
              <label>🚙 Registration Number *</label>
              <input
                type="text"
                name="Registration"
                value={vehicleData.Registration}
                onChange={handleVehicleDataChange}
                className="form-input-narrow"
                placeholder="e.g., KAA 123A"
              />
            </div>

            <div className="form-group-icon">
              <label>🏭 Make *</label>
              <input
                type="text"
                name="Make"
                value={vehicleData.Make}
                onChange={handleVehicleDataChange}
                className="form-input-narrow"
                placeholder="e.g., Toyota"
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group-icon">
              <label>🚗 Model *</label>
              <input
                type="text"
                name="Model"
                value={vehicleData.Model}
                onChange={handleVehicleDataChange}
                className="form-input-narrow"
                placeholder="e.g., Corolla"
              />
            </div>

            <div className="form-group-icon">
              <label>📅 Year of Manufacture *</label>
              <input
                type="number"
                name="YOM"
                value={vehicleData.YOM}
                onChange={handleVehicleDataChange}
                className="form-input-narrow"
                placeholder="e.g., 2020"
                min="1900"
                max={new Date().getFullYear() + 1}
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group-icon">
              <label>🔧 Chassis Number *</label>
              <input
                type="text"
                name="Chassis"
                value={vehicleData.Chassis}
                onChange={handleVehicleDataChange}
                className="form-input-narrow"
                placeholder="Enter chassis number"
              />
            </div>

            <div className="form-group-icon">
              <label>⚙️ Engine Number *</label>
              <input
                type="text"
                name="Engine"
                value={vehicleData.Engine}
                onChange={handleVehicleDataChange}
                className="form-input-narrow"
                placeholder="Enter engine number"
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group-icon">
              <label>🔢 CC</label>
              <input
                type="number"
                name="CC"
                value={vehicleData.CC}
                onChange={handleVehicleDataChange}
                className="form-input-narrow"
                placeholder="e.g., 1500"
              />
            </div>

            <div className="form-group-icon">
              <label>🎨 Color *</label>
              <input
                type="text"
                name="Color"
                value={vehicleData.Color}
                onChange={handleVehicleDataChange}
                className="form-input-narrow"
                placeholder="e.g., White"
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group-icon">
              <label>📆 Commencement Date *</label>
              <input
                type="date"
                name="CommencementDate"
                value={vehicleData.CommencementDate}
                onChange={handleVehicleDataChange}
                className="form-input-narrow"
              />
            </div>

            <div className="form-group-icon">
              <label>📆 Expiry Date *</label>
              <input
                type="date"
                name="ExpiryDate"
                value={vehicleData.ExpiryDate}
                onChange={handleVehicleDataChange}
                className="form-input-narrow"
                readOnly
              />
            </div>
          </div>

          <PolicyDocuments policy={policy} />

          <div className="form-actions-center">
            <button type="button" className="secondary-btn-narrow" onClick={handleCancel}>
              ← Cancel
            </button>
            <button
              type="button"
              className="primary-btn-narrow"
              onClick={handleUpdatePolicy}
              disabled={submitting}
            >
              {submitting ? '⏳ Updating...' : '✓ Update Policy'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPolicy;
